import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import Icon from 'components/Icon'

import CheckboxDropdown from 'components/CheckboxDropdown'
import Message from './components/Message'
import Delivery from './components/Delivery'
import Audition from './components/Audition'
import Event from './components/Event'
import Feedback from './components/Feedback'
import FeedbackRequest from './components/FeedbackRequest'
import Task from './components/Task'
import AuditionScopeOverview from './components/AuditionScopeOverview'
import messages from "./messages"
import { FormattedMessage, useIntl } from 'react-intl'
import useCurrentUser from 'hooks/useCurrentUser'
import useLocalStorageState from 'hooks/useLocalStorageState'
import { Button } from '@arnsbogroup/react-voicearchive'

const Components = {
  Message,
  Event,
  Feedback,
  FeedbackRequest,
  Delivery,
  Audition,
  Task,
  AuditionScopeOverview
}

const OrderHistory = ({ order, onHide, fetchedEvents, onRefetch }) => {
  const logRef = useRef(null)
  const inputRef = useRef(null)
  const [message, setMessage] = useState('')
  const [events, setEvents] = useState(fetchedEvents ?? null)
  const [loading, setLoading] = useState(false)

  const [viewOptions, setViewOptions] = useLocalStorageState({
    Message: true,
    Event: false,
    Delivery: false,
  }, 'OrderHistory.viewOptions')

  const { formatMessage } = useIntl()
  const { image, name, email } = useCurrentUser()

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight
    }
  }, [events])

  const onSend = useCallback((event) => {
    event.preventDefault()

    const body = message

    setMessage('')
    setLoading(true)

    if (!message.length) {
      return window.alert(messages.alert)
    }

    const nextEvent = {
      type: 'Message',
      title: '',
      body,
      by: { image, name, email },
      timestamp: +Date.now() / 1000,
    }
    
    fetch(`/message/add?body=${encodeURIComponent(body)}&orderId=${order.uid}`)
      .then((response) => response.json())
      .then(({ errors, success }) => {
        if (errors) {
          return window.alert(errors.join(' '))
        } else {
          setEvents([...events, nextEvent])
        }
      }).catch((err) => window.alert(err))
      .finally(() => setLoading(false))
  }, [message])


  const sortedEvents = useMemo(() => {
    return (events || []).sort((a, b) => {
      return (a.timestamp < b.timestamp) ? -1 : 1
    }).map(({ type, ...props }, i) => {
      const Component = Components[type]

      if (type in viewOptions && !viewOptions[type]) {
        return null
      }

      return (
        <Component
          key={`item-${i}`}
          last={i === events.length - 1}
          orderId={order.uid}
          onRefetch={onRefetch}
          {...props}
        />
      )
    })
    .filter(Boolean)
  }, [events, viewOptions, order])

  return (
    <div className="w-full h-full flex flex-col">
      <div className="shadow bg-gray-50 w-full px-1 md:px-2 xl:px-4 py-2 xl:py-3 text-xs font-medium text-gray-600 uppercase tracking-wider flex justify-between items-center">
        <span>
          <span><FormattedMessage {...messages.history} /></span>
          <Icon icon="sync" onClick={onRefetch} className="cursor-pointer hover:text-gray-300 ml-2" />
        </span>

        <span className="space-x-2">
          <CheckboxDropdown options={viewOptions} onChange={setViewOptions} />

          { onHide &&
            <Icon icon="times" onClick={onHide} className="cursor-pointer hover:text-gray-300"/>
          }
        </span>
      </div>

      { events  &&
        <>
          <div className="overflow-y-scroll flex-grow" ref={logRef}>
            <ul className="-mb-8 p-4">
              { sortedEvents }
            </ul>
          </div>

          <div className='min-h-24'>
            <form className="space-y-1 p-2 xl:p-4 m-0" onSubmit={onSend}>
              <div className="flex justify-between items-center">
                <Button
                  variant={message.length ? 'solid' :'outline'}
                  size='sm'
                  disabled={loading || !message.length}
                  type="submit"
                >
                  <FormattedMessage {...messages.send}/> 
                  <Icon 
                    icon={loading ? 'spinner-third' : 'paper-plane'} 
                    className={loading ? 'animate-spin' : ''} 
                  />
                </Button>
              </div>

              <textarea
                ref={inputRef}
                className="px-4 py-2 rounded-sm text-sm border border-gray-200 w-full focus:border-blue-500"
                placeholder={formatMessage(messages.typeMessage)}
                value={message}
                onFocus={() => inputRef.current.classList.add('h-40')}
                onBlur={() => {
                  if (inputRef.current && !message.length) {
                    inputRef.current.classList.remove('h-40')
                  }
                }}
                onChange={({ target: { value } }) => setMessage(value)}
                onKeyDown={(event) => {
                  if (event.metaKey && event.key == 'Enter') {
                    event.preventDefault()
                    onSend(event)
                  }
                }}
              />
            </form>
          </div>
        </>
      }
    </div>
  )
}

export default OrderHistory